import { Suspense, useMemo } from "react"
import { Box, Loader, MultiWidget, Stack, Typography } from "@tm/components"
import { useLocalization } from "@tm/localization"
import StartFooterComponent from "@bundles/trader/wm/components/start-footer/component"
import { WidgetFrame } from "@bundles/offers"
import { getComponentFromBundle, notUndefinedOrNull } from "@tm/utils"
import comp from "@bundles/trader/wm/components/papercatalogs-link"
import IFrameWidget from "@bundles/misc/components/iframe-widget"
import LinkWidgetComponent from "@bundles/trader/wm/components/link-widget"
import { getModuleFromUserContext, useUser } from "@tm/context-distribution"
import { UserModuleType } from "@tm/models"
import AdTile from "@bundles/trader/wm/components/ad-tile"
import manager from "@bundles/parts/components/manager"
import {
    StyledCustomerSearchWidget,
    StyledDirectSearchWidget,
    StyledStack,
    StyledTyresWidget,
    StyledUniSearchWidget,
    StyledVehicleWidget,
} from "./StyledComponents"
import { startWMChannel } from "./channel"

const PaperCatalogsLink = getComponentFromBundle(comp, startWMChannel)
const TrainingsWidget = getComponentFromBundle(IFrameWidget, startWMChannel)
const LinkWidget = getComponentFromBundle(LinkWidgetComponent, startWMChannel)
const AdTileWidget = getComponentFromBundle(AdTile, startWMChannel)
const PartsManager = getComponentFromBundle(manager, startWMChannel)

function DirectSearch() {
    const { translateText } = useLocalization()
    return (
        <Stack gap={0.5}>
            <Typography variant="body2">{`${translateText(1046)}:`}</Typography>
            <StyledDirectSearchWidget directSearchRoute="/:workTaskId/parts/direct/list/direct" contentOnly />
        </Stack>
    )
}

function CustomerSearch() {
    return <StyledCustomerSearchWidget listRoute="/:workTaskId/customer/list" detailsRoute="/:workTaskId/customer/details/:customerId?/" />
}

function UniPartsSearch() {
    return (
        <StyledUniSearchWidget
            universalListRoute="/:workTaskId/parts/universal/list/uninode"
            universalSearchRoute="/:workTaskId/parts/universal/list/unisearch"
            hideTree
        />
    )
}

function VehicleWidget() {
    return <StyledVehicleWidget />
}

function TyresSearch() {
    return <StyledTyresWidget />
}

export function StartWM() {
    return (
        <Suspense fallback={<Loader />}>
            <StartWMComponent />
        </Suspense>
    )
}

function StartWMComponent() {
    const { translateText } = useLocalization()
    const { userContext } = useUser()
    const traderDescription = userContext?.system.traderDescription
    const TRADER_TYPES = {
        NL: "WMKAT+ NL",
        DK: "WMKAT+ DK",
    }

    const tabs = useMemo(() => {
        return [
            {
                key: "vehicleWidget",
                label: translateText(99),
                tabContent: VehicleWidget,
            },
            {
                key: "directSearch",
                label: translateText(389),
                tabContent: DirectSearch,
            },
            getModuleFromUserContext(userContext, UserModuleType.UnipartsPremium)
                ? {
                      key: "uniPartsSearch",
                      label: translateText(1009),
                      tabContent: UniPartsSearch,
                  }
                : undefined,
            getModuleFromUserContext(userContext, UserModuleType.TMTires)
                ? {
                      key: "tyres",
                      label: translateText(13572),
                      tabContent: TyresSearch,
                  }
                : undefined,
            {
                key: "customer",
                label: translateText(107),
                tabContent: CustomerSearch,
            },
        ].filter(notUndefinedOrNull)
    }, [translateText, userContext])

    return (
        <StyledStack direction="column" flex={1} gap={2} sx={{ backgroundColor: "#efefef" }}>
            <Stack direction="row">
                <MultiWidget
                    widgets={tabs}
                    enablePaper
                    enableContentDivider
                    paperSx={{ height: "auto", minHeight: "620px", width: "540px", margin: 4 }}
                    variant="buttons"
                />
                <Stack mt={4} mb={4} justifyContent="space-between">
                    <AdTileWidget
                        prodUrl="https://wmkatsrvcneu.wmkat.de/wks/api/tileContentsByTileType/65"
                        devUrl="https://testsvc.wmkat.de/wks/api/tileContentsByTileType/65"
                    />
                    <Stack direction="row">
                        {traderDescription === TRADER_TYPES.NL && (
                            <LinkWidget image="/styles/wm/images/Kentekenplaten.jpg" textId="13574" target={{ url: "/:workTaskId/dop" }} />
                        )}
                        {traderDescription === TRADER_TYPES.DK && (
                            <LinkWidget
                                image="/styles/wm/images/lieferantenservice.png"
                                textId="13409"
                                url="https://tilmeld.leverandoerservice.dk/LS?id=-1046550440&pbs=9c804e9a62bd40e3b933aa6c5ed188b6&kundenrLabel=Kundenr.&cvr=&dbnr=&knmin=&knmax="
                            />
                        )}
                        {traderDescription !== TRADER_TYPES.DK && traderDescription !== TRADER_TYPES.NL && (
                            <TrainingsWidget
                                token="Wm"
                                prodUrl="https://widget.wmkat.de/Startpage/Repmaster?custNr={0}"
                                devUrl="https://widget-test.wmkat.de/Startpage/Repmaster?custNr={0}"
                                className="trainings"
                            />
                        )}
                        <PaperCatalogsLink />
                    </Stack>
                </Stack>
            </Stack>
            {/* Offers */}
            {getModuleFromUserContext(userContext, UserModuleType.OfferCarousel) && (
                <Box height="575px">
                    <WidgetFrame
                        showCarouselOffers
                        showNormalOffers={false}
                        variant="wm"
                        storeId="offers"
                        height={33}
                        params={{
                            async: true,
                            mode: 1,
                            relatedModuleSubId: 129,
                        }}
                    />
                </Box>
            )}
            {/* Footer */}
            <StartFooterComponent />
            {getModuleFromUserContext(userContext, UserModuleType.UnipartsPremium) && <PartsManager handleDetailsRequest={false} />}
        </StyledStack>
    )
}
